import { ReactElement, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import { USGA_SCORE_TYPES } from '../../types/UsgaPost';

import type { UsgaScoreType } from '../../types/UsgaPost';

interface EventScoreUsgaPostAllFormProps {
  /** Scores that can be posted as they are. */
  readyCount: number;
  /** Total-only scores, which need their adjusted score confirmed one at a time. */
  totalOnlyCount: number;
  onSubmit: (scoreType: UsgaScoreType) => Promise<void>;
  onCancel: () => void;
}

const SCORE_TYPE_OPTIONS: { value: UsgaScoreType; label: string }[] = [
  { value: USGA_SCORE_TYPES.COMPETITION, label: 'Competition' },
  { value: USGA_SCORE_TYPES.HOME, label: 'Home' },
  { value: USGA_SCORE_TYPES.AWAY, label: 'Away' },
];

/** The inline panel for posting every ready score on a day with one score type. */
export default function EventScoreUsgaPostAllForm({ readyCount, totalOnlyCount, onSubmit, onCancel }: EventScoreUsgaPostAllFormProps): ReactElement {
  const [scoreType, setScoreType] = useState<UsgaScoreType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (!scoreType) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await onSubmit(scoreType);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn\'t send the request.');
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={1}>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={scoreType}
        onChange={(_e, value: UsgaScoreType | null) => { if (value) setScoreType(value); }}
        aria-label="Score type"
      >
        {SCORE_TYPE_OPTIONS.map(({ value, label }) => <ToggleButton key={value} value={value}>{label}</ToggleButton>)}
      </ToggleButtonGroup>
      {totalOnlyCount > 0 && (
        <Typography variant="bodySmall" color="text.secondary">
          {totalOnlyCount === 1 ? '1 total-only score isn\'t included' : `${totalOnlyCount} total-only scores aren't included`} — post
          {totalOnlyCount === 1 ? ' it' : ' them'} one at a time to confirm the adjusted score.
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="contained" disabled={!scoreType || isSubmitting} onClick={handleSubmit}>
          {isSubmitting ? <CircularProgress size="1.25em" /> : `Post ${readyCount} ${readyCount === 1 ? 'score' : 'scores'}`}
        </Button>
        <Button size="small" onClick={onCancel} disabled={isSubmitting}>Cancel</Button>
      </Stack>
    </Stack>
  );
}
